import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  FiSettings,
  FiUsers,
  FiDatabase,
  FiPackage,
  FiLayers,
  FiKey,
} from 'react-icons/fi';

import AssetSetup from '../Asset-Setup/AssetSetup';
import CompanySetup from '../Company-Setup/CompanySetup';
import ParticipantSetup from '../Participant-Setup/ParticipantSetup';
import ResourceSetup from '../Resource-Setup/ResourceSetup';
import AssetGroup from '../User-Management/AssetGroup/AssetGroupSetup/AssetGroupSetup';
import UserSetup from '../User-Management/User/UserSetup/UserSetup';
import UserGroup from '../User-Management/UserGroup/UserGroupSetup/UserGroupSetup';
import PogSetup from '../User-Management/POG/POGSetup/POGSetup';
// import EntitlementsSetup from '../EntitlementsSetup/EntitlementsSetup';
import MetricSetup from '../Metric-Setup/MetricSetup';
import MetricGroup from '../MetricGroup-Setup/MetricGroup-Setup';
import PIScreen from '../PIScreenSetup/PIScreenSetup';
// import MetricScreen from '../Metric-Screen/MetricScreen';
import MetricScreen from '../Metric-Screen/MetricScreen';

import { useEntitlements } from '../../DataContext/EntitlementsContext';

const AdministrationTab: React.FC = () => {
  const { entitlements } = useEntitlements();
  const isProduction = ["prd", "production"].includes(
    (process.env.REACT_APP_DD_ENV || "").toLowerCase()
  );

  const [activeTopTab, setActiveTopTab] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<string | null>(null);

  const tabSwitchGuardRef = useRef<() => boolean | Promise<boolean>>();
  const [piPrefillResource, setPiPrefillResource] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentTop, setContentTop] = useState<number | null>(null);

  useLayoutEffect(() => {
    const updateContentTop = () => {
      const top = contentRef.current?.getBoundingClientRect().top;
      if (top !== undefined) {
        setContentTop((currentTop) => (currentTop === Math.round(top) ? currentTop : Math.round(top)));
      }
    };

    updateContentTop();
    window.addEventListener("resize", updateContentTop);
    return () => window.removeEventListener("resize", updateContentTop);
  }, [activeTopTab, activeSubTab]);

  const hasAccess = (path: string) => {
    return entitlements.some(
      (entitlement) =>
        entitlement.po_path === path &&
        ['Read', 'Write', 'Admin'].includes(entitlement.privilege)
    );
  };
  const hasAdminAccess = (path: string) => {
    return entitlements.some(
      (entitlement) =>
        entitlement.po_path === path &&
        entitlement.privilege === "Admin"
    );
  };

  const handleTopTabClick = async (tab: string) => {
    if (tabSwitchGuardRef.current) {
      const canSwitch = await tabSwitchGuardRef.current();
      if (!canSwitch) return;
    }

    // 🔹 Collapse if already selected
    if (activeTopTab === tab) {
      setActiveTopTab(null);
      setActiveSubTab(null);
      return;
    }

    setActiveTopTab(tab);

    // default first subtab
    if (tab === 'Assets') setActiveSubTab('AssetSetup');
    if (tab === 'Entitlements') setActiveSubTab('UserSetup');
    if (tab === 'Metrics') setActiveSubTab('MetricSetup');
  };

  const handleSubTabClick = async (tabId: string) => {
    if (tabSwitchGuardRef.current) {
      const canSwitch = await tabSwitchGuardRef.current();
      if (!canSwitch) return;
    }
    setActiveSubTab(tabId);
  };

  const goToPIScreen = async (resourceName?: string) => {
    if (tabSwitchGuardRef.current) {
      const canSwitch = await tabSwitchGuardRef.current();
      if (!canSwitch) return;
    }

    setActiveTopTab('Metrics');
    setActiveSubTab('PIScreen');
    setPiPrefillResource(resourceName ?? null);
  };

  const subTabsMap: Record<string, any[]> = {
    Assets: [
      { id: 'AssetSetup', label: 'Asset', path: '/administration/asset-setup', icon: <FiPackage /> },
      { id: 'CompanySetup', label: 'Company', path: '/administration/company-setup', icon: <FiSettings /> },
      { id: 'ParticipantSetup', label: 'Participant', path: '/administration/participant-setup', icon: <FiUsers /> },
      { id: 'ResourceSetup', label: 'Resource', path: '/administration/resource-setup', icon: <FiDatabase /> },
    ],
    Entitlements: [
      { id: 'AssetGroup', label: 'Asset Group', path: '/administration/asset-group-setup', icon: <FiLayers /> },
      { id: 'UserSetup', label: 'User Setup', path: '/administration/user-setup', icon: <FiUsers /> },
      { id: 'Role', label: 'Role', path: '/administration/user-group-setup', icon: <FiUsers /> },
      { id: 'EndpointsGroup', label: 'Endpoints Group', path: '/administration/permission-group-setup', icon: <FiDatabase /> },
      // { id: 'Entitlements', label: 'Entitlements', path: '/administration/entitlements', icon: <FiKey /> },
    ],
    Metrics: [
      { id: 'MetricSetup', label: 'Metric Setup', path: '/administration/metric-setup', icon: <FiDatabase /> },
      { id: 'MetricGroup', label: 'Metric Group', path: '/administration/metric-group', icon: <FiDatabase /> },
      { id: 'PIScreen', label: 'PI Screen', path: '/administration/pi-screen', icon: <FiDatabase /> },
      ...(!isProduction
        ? [{ id: 'MetricScreen', label: 'Metric Screen', path: '/administration/metric-setup', icon: <FiDatabase /> }]
        : []),
    ],
  };

  const renderTabContent = () => {
    switch (activeSubTab) {

      case 'AssetSetup':
        return (
          <AssetSetup
            registerTabSwitchGuard={(fn) => (tabSwitchGuardRef.current = fn)}
            canDelete={hasAdminAccess('/administration/asset-setup')}
          />
        );
      case 'CompanySetup':
        return <CompanySetup
          registerTabSwitchGuard={(fn) => (tabSwitchGuardRef.current = fn)}
          canDelete={hasAdminAccess('/administration/company-setup')}
        />
      case 'ParticipantSetup':
        return <ParticipantSetup
          registerTabSwitchGuard={(fn) => (tabSwitchGuardRef.current = fn)}
          canDelete={hasAdminAccess('/administration/participant-setup')}
        />
      case 'ResourceSetup':
        return (
          <ResourceSetup
            registerTabSwitchGuard={(fn) => (tabSwitchGuardRef.current = fn)}
            goToPIScreen={goToPIScreen}
            canDelete={hasAdminAccess('/administration/resource-setup')}
          />
        );
      case 'AssetGroup':
        return (
          <AssetGroup
            registerTabSwitchGuard={(fn) => (tabSwitchGuardRef.current = fn)}
            canDelete={hasAdminAccess('/administration/asset-group-setup')}
          />
        );
      case 'UserSetup':
        return (
          <UserSetup
            registerTabSwitchGuard={(fn) => (tabSwitchGuardRef.current = fn)}
            canDelete={hasAdminAccess('/administration/user-setup')}
          />
        );
      case 'Role':
        return (
          <UserGroup
            registerTabSwitchGuard={(fn) => (tabSwitchGuardRef.current = fn)}
            canDelete={hasAdminAccess('/administration/user-group-setup')}
          />
        );
      case 'EndpointsGroup':
        return (
          <PogSetup
            registerTabSwitchGuard={(fn) => (tabSwitchGuardRef.current = fn)}
            canDelete={hasAdminAccess('/administration/permission-group-setup')}
          />
        );
      // case 'Entitlements':
      //   return <EntitlementsSetup registerTabSwitchGuard={(fn) => (tabSwitchGuardRef.current = fn)} />;
      case 'MetricSetup':
        return (
          <MetricSetup
            registerTabSwitchGuard={(fn) => (tabSwitchGuardRef.current = fn)}
            canDelete={hasAdminAccess('/administration/metric-setup')}
          />
        );
      case 'MetricGroup':
        return (
          <MetricGroup
            registerTabSwitchGuard={(fn) => (tabSwitchGuardRef.current = fn)}
            canDelete={hasAdminAccess('/administration/metric-group')}
          />
        );
      case 'PIScreen':
        return (
          <PIScreen
            registerTabSwitchGuard={(fn) => (tabSwitchGuardRef.current = fn)}
            prefillResourceName={piPrefillResource}
            clearPrefill={() => setPiPrefillResource(null)}
            canDelete={hasAdminAccess("/administration/pi-screen")}
          />
        );
      case 'MetricScreen':
        return isProduction ? null : <MetricScreen />;
      default:
        return null;
    }
  };

  return (
    <div className="mx-10 mt-10 font-poppins">
      <h1 className="text-2xl font-bold text-gray-700 mb-6">Administration</h1>

      {/* 🔹 Top Level Row */}
      <div className="flex items-center gap-4 bg-gray-50 rounded-lg px-4 py-3 mb-4">

        {/* Show all top tabs if none selected */}
        {!activeTopTab &&
          ['Assets', 'Entitlements', 'Metrics'].map((tab) => (
            <button
              key={tab}
              onClick={() => handleTopTabClick(tab)}
              className="px-5 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
            >
              {tab}
            </button>
          ))}

        {/* If selected → show only selected + subtabs */}
        {activeTopTab && (
          <>
            {/* Selected Top Tab */}
            <button
              onClick={() => handleTopTabClick(activeTopTab)}
              className="px-5 py-2 rounded-lg bg-white text-gray-800 border-b-2 border-red-300 shadow-sm"
            >
              {activeTopTab}
            </button>

            {/* Sub Tabs */}
            <div className="flex gap-3 ml-4 overflow-x-auto">
              {subTabsMap[activeTopTab]
                .filter((tab) => hasAccess(tab.path))
                .map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleSubTabClick(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md whitespace-nowrap transition ${
                      activeSubTab === tab.id
                        ? 'bg-white text-red-500 border-b-2 border-red-300 shadow'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
            </div>
          </>
        )}
      </div>

      {/* Content */}
      {/* <div className="bg-white shadow-lg p-6 rounded-lg">
        {renderTabContent()}
      </div> */}
      <div className="bg-white shadow-lg rounded-lg min-h-[400px] flex items-center justify-center">
        {!activeTopTab ? (
          <div className="text-center text-gray-500">
            <h2 className="text-xl font-semibold mb-2">
              Select a category tab
            </h2>
            <p className="text-sm text-gray-400">
              Choose Assets, Entitlements, or Metrics to browse available options.
            </p>
          </div>
        ) : (
          <div
            ref={contentRef}
            className="w-full p-6"
            style={contentTop === null ? undefined : {
              ["--admin-tab-panel-height" as string]: `max(0px, calc(100dvh - ${contentTop + 64}px))`,
              ["--admin-tab-grid-height" as string]: `max(0px, calc(100dvh - ${contentTop + 152}px))`,
            }}
          >
            {renderTabContent()}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdministrationTab;